<template>
  <div class="document-container columns" style="padding-top: 0">
    <div class="column is-10">
      <div class="document">
        <div class="header">
          <es-header>
            <template #header>
              <div class="header-title">Location Summary</div>
            </template>
            <template #actions>
              <ul>
                <li v-if="isCepRosterAvailable"
                  class="p-narrow no-border">
                  <div class="list-content">
                    <a
                      @click.prevent="generateCepRoster()"
                      :class="{ 'disabled':
                        !cepRosterGenerationFinished
                        || loading
                        || loadingSummary
                        || loadingSummaryError }"
                      class="generate-cep-roster-button">
                      Generate CEP Roster CSV
                    </a>
                  </div>
                </li>
                <li v-if="refreshIsAvailable"
                  class="p-narrow no-border">
                  <div class="list-content">
                    <a
                      @click="refreshPopulation"
                      :class="{ 'disabled':
                          loading
                          || loadingError
                          || loadingSummary
                          || loadingSummaryError
                          || isPopulationDateInvalid
                          || isCutOffDateExceeded
                          || cepSummary.populationStatus === 'A'
                          || cepSummary.populationStatus === 'S'
                          || cepSummary.recalculateStatus === 'I'}"
                      title="Click to refresh data.">
                      <font-awesome-icon :icon="['fas', 'sync-alt']" class="lg"/>
                    </a>
                  </div>
                </li>
              </ul>
            </template>
          </es-header>
        </div>
        <div v-if="loading || loadingSummary" style="padding: 6.5rem">
          <div class="icon">
            <es-loader/>
          </div>
          <p></p>
          <h4 style="font-size: 14px">
            Calculating population data...
          </h4>
        </div>
        <div
          v-else-if="
            schoolYear === currentSchoolYear
            && cepSummary.recalculateStatus === 'I'"
          style="padding: 6.5rem">
          <div class="icon">
            <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="4x"/>
          </div>
          <p></p>
          <h4 style="font-size: 14px">
            Population refresh is in progress. Click the Refresh button
            <a @click="reloadPopulationData">
              <font-awesome-icon :icon="['fas', 'sync-alt']" class="lg"/>
            </a>
            to get the most current data.
          </h4>
        </div>
        <div v-else class="content">
          <table class="table is-hoverable is-fullwidth">
            <es-sorter
              :apiSorter="apiSorter"
              :columnHeaders="columnHeaders"
              @updateSorter="updateSorter"
            />
            <tbody v-if="loadingError || loadingSummaryError">
              <tr>
                <td colspan="100%" style="text-align: center">
                  <es-exception
                    exceptionMessage="There was an issue. Please try again or contact your administrator."/>
                </td>
              </tr>
            </tbody>
            <tbody
              v-else-if="
                schoolYear === currentSchoolYear
                && !isStateUser
                && !isPopulationDateInvalid
                && cepSummary.recalculateStatus === 'E'
                && cepSummary.populationStatus !== 'A'
                && cepSummary.populationStatus !== 'S'
                && cepSummary.recalculateStatus !== 'I'
                && hasModule('DRMCH:CEP:POPULATION:SUBMIT')">
              <tr>
                <td colspan="100%" style="text-align: center; padding: 6.5rem;">
                  <div class="icon">
                    <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="3x"/>
                  </div>
                  <div class="is-size-5">
                    An error has occurred during the refresh process. Click the Refresh button
                    <a @click="refreshPopulation">
                      <font-awesome-icon :icon="['fas', 'sync-alt']" class="lg"/>
                    </a>
                    to get the most current data.
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody
              v-else-if="
                schoolYear === currentSchoolYear
                && !isStateUser
                && !isPopulationDateInvalid
                && cepSummary.recalculateStatus === 'N'
                && cepSummary.populationStatus !== 'A'
                && cepSummary.populationStatus !== 'S'
                && cepSummary.recalculateStatus !== 'I'
                && hasModule('DRMCH:CEP:POPULATION:SUBMIT')">
              <tr>
                <td colspan="100%" style="text-align: center; padding: 6.5rem;">
                  <div class="icon">
                    <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="3x"/>
                  </div>
                  <div class="is-size-5">
                    Click the Refresh button
                    <a @click="refreshPopulation">
                      <font-awesome-icon :icon="['fas', 'sync-alt']" class="lg"/>
                    </a>
                    to get the most current data.
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="!locations.length">
              <tr>
                <td colspan="100%" style="text-align: center">
                  <es-exception exceptionMessage="No results found."/>
                </td>
              </tr>
            </tbody>
            <template v-else>
              <tbody>
                <tr v-for="location in locations" :key="location.localCode">
                  <td>
                    <es-eligibility-marker size="small" :value="location.totalISP" />
                  </td>
                  <td>{{ location.localCode }}</td>
                  <td>{{ location.locationName }}</td>
                  <td>
                    {{ `${location.identifiedCount}/${location.enrolledCount}` }}</td>
                  <td>{{ (location.totalISP * 100).toFixed(2) }}</td>
                  <td>
                    <!-- TODO: cleanup this section. Call computd prop. -->
                    <span
                      v-if="location.approvalStatus === 'S'"
                      class="text-nowrap"
                      title="Submitted">
                      <font-awesome-icon :icon="['fas', 'dot-circle']" class="has-text-info"/>
                      Submitted
                    </span>
                    <span
                      v-if="location.approvalStatus === 'P'"
                      class="text-nowrap"
                      title="Pending">
                      <font-awesome-icon
                        :icon="['fas', 'exclamation-circle']"
                        class="has-text-warning"/>
                      Pending
                    </span>
                    <span
                      v-if="location.approvalStatus === 'A'"
                      class="text-nowrap"
                      title="Accepted">
                      <font-awesome-icon :icon="['fas', 'check-circle']" class="has-text-success"/>
                      Accepted
                    </span>
                    <span
                      v-if="location.approvalStatus === 'R'"
                      class="text-nowrap"
                      title="Rejected">
                      <font-awesome-icon :icon="['fas', 'ban']" class="has-text-danger"/>
                      Rejected
                    </span>
                  </td>
                  <td>
                    <div class="is-flex flex-align--center border-left">
                      <div
                        class="action-padding"
                        v-if="
                          isStateUser
                          && schoolYear === currentSchoolYear
                          && hasModule('DRMCH:CEP:POPULATION:APPROVE')">
                        <a
                          :class="{ 'disabled': checkRejectStatus(location)}"
                          @click="rejectSchool(location.stateLocationId)"
                          title="Reject School">
                          <font-awesome-icon :icon="['fas', 'ban']" size="lg"/>
                        </a>
                      </div>
                      <div
                        class="action-padding"
                        v-if="
                          isStateUser
                          && schoolYear === currentSchoolYear
                          && hasModule('DRMCH:CEP:POPULATION:APPROVE')">
                        <a
                          :class="{ 'disabled': checkApprovalStatus(location)}"
                          @click="approveSchool(location.stateLocationId)"
                          title="Accept School">
                          <font-awesome-icon :icon="['fas', 'check']" size="lg"/>
                        </a>
                      </div>
                      <div class="action-padding">
                        <router-link
                          :to="{
                            path: `/directMatch/cep/population/${location.stateLocationId}`,
                            query: {
                              locationGroupId,
                              schoolYear,
                            },
                          }"
                          title="View Details"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'angle-double-right']"
                            size="2x"
                            title="View Details"/>
                        </router-link>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="100%">
                    <es-pager :totalCount="totalCount"/>
                  </td>
                </tr>
              </tfoot>
            </template>
          </table>
          <div
            v-if="
              !loading
              && !loadingSummary
              && !loadingError
              && canSubmit"
            class="certify-row is-flex"
          >
            <div class="certify-description is-pulled-right is-flex flex-direction--column">
              <label class="checkbox has-text-left">
                <input
                  type="checkbox"
                  style="margin-right:0.5rem"
                  v-model="certified"
                  :disabled="checkSubmissionStatus"
                  @click="resetCertify">
                I certify the information entered for all schools in this application is correct and, if participating, will be used for CEP population data if applicable.
              </label>
              <div
                v-if="certifyError"
                class="certify-text help is-danger has-text-left">
                Please check the checkbox to proceed to submit and certify population data.
              </div>
            </div>
            <button
              class="button is-primary"
              @click="submitPopulation"
              :disabled="checkSubmissionStatus">
              Submit &amp; Certify Population
            </button>
          </div>
          <div
            v-if="
              !loading
              && !loadingError
              && !loadingSummaryError
              && schoolYear === currentSchoolYear
              && isStateUser
              && locations.length
              && hasModule('DRMCH:CEP:POPULATION:APPROVE')"
            class="certify-row is-flex">
            <button
              class="button is-primary"
              @click="reopenSubmission"
              :disabled="checkReopenStatus">
              Reopen Submission
            </button>
          </div>
        </div>
      </div>
      <cep-legend />
    </div>
    <div class="column is-2">
      <cep-my-activity
        ref="myActivity"
        :locationGroupId="locationGroupId"
        :schoolYear="schoolYear"
        @onRefreshMyActivity="onRefreshMyActivity" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faExclamationTriangle,
} from '@fortawesome/pro-light-svg-icons';
import {
  faCheckCircle,
  faDotCircle,
  faExclamationCircle,
  faSyncAlt,
  faCheck,
  faBan,
  faAngleDoubleRight,
} from '@fortawesome/pro-solid-svg-icons';

import CepLegend from '@/components/CepLegend.vue';
import CepMyActivity from '@/components/CepMyActivity.vue';
import EsEligibilityMarker from '@/components/EsEligibilityMarker.vue';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsSorter from '@/components/EsSorter.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleRight,
  faBan,
  faCheck,
  faCheckCircle,
  faDotCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faSyncAlt,
);

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    CepLegend,
    CepMyActivity,
    EsEligibilityMarker,
    EsException,
    EsHeader,
    EsLoader,
    EsPager,
    EsSorter,
  },
  props: {
    cepSummary: {
      type: Object,
    },
    loadingSummary: {
      type: Boolean,
    },
    loadingSummaryError: {
      type: Boolean,
    },
    locationGroupId: {
      type: [String, Number],
    },
    page: {
      type: Number,
      default: 1,
    },
    schoolYear: {
      type: Number,
    },
    sortField: {
      type: String,
      default: 'name',
    },
    sortOrder: {
      type: String,
      default: 'ASC',
    },
  },
  data() {
    return {
      certified: null,
      certifyError: null,
      cepRosterGenerationFinished: false,
      loading: false,
      loadingError: false,
      locations: [],
      totalCount: null,
      apiSorter: {},
      columnHeaders: [],
    };
  },
  async created() {
    await this.loadPersonTypes();
    this.getLocations();

    this.columnHeaders = [
      {
        header: null,
        field: null,
      },
      {
        header: `${this.getFieldLabel(`PER:${this.defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`)} Code`,
        field: 'localCode',
        order: null,
        sortable: true,
      },
      {
        header: `${this.getFieldLabel(`PER:${this.defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`)} Name`,
        field: 'name',
        order: 'ASC',
        sortable: true,
      },
      {
        header: 'Identified/Enrolled',
        field: 'identifiedCount',
        order: null,
        sortable: true,
      },
      {
        header: 'ISP %',
        field: 'totalISP',
        order: null,
        sortable: true,
      },
      {
        header: 'Accepted Status',
        field: 'approvalStatus',
        order: null,
        sortable: true,
      },
      {
        header: 'Actions',
        field: null,
      },
    ];
  },
  watch: {
    $route() {
      this.getLocations();
      if (this.certified) {
        this.certified = false;
      }
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    ...mapGetters({
      isStateUser: 'CEPPopulationApprovalUser',
      properties: 'propertiesModule/properties',
    }),
    checkSubmissionStatus() {
      const { populationStatus } = this.cepSummary;

      return (
        this.isPopulationDateInvalid
        || (this.isStateUser && populationStatus !== 'S')
        || (!this.isStateUser
          && (
            populationStatus === 'S'
            || populationStatus === 'A'
            || populationStatus === 'R'
          ))
        || this.cepSummary.recalculateStatus === 'I');
    },
    checkReopenStatus() {
      const { populationStatus } = this.cepSummary;
      return (
        this.isPopulationDateInvalid
        || populationStatus !== 'S'
        || this.cepSummary.recalculateStatus !== 'C'
      );
    },
    currentSchoolYear() {
      return Number(this.$store.state.propertiesModule.properties['CEP-CEP_CURRENT_SCHOOL_YEAR']);
    },
    electionStatus() {
      return this.cepSummary.electionStatus;
    },
    isPopulationDateInvalid() {
      const today = new Date().getTime();

      return moment(today).isAfter(moment(this.properties['CEP-POPULATION_SUBMISSION_DUE_DATE']), 'day')
        || moment(today).isBefore(this.properties['CEP-POPULATION_SUBMISSION_OPEN_DATE']);
    },
    isCutOffDateExceeded() {
      const today = new Date().getTime();
      return moment(today).isAfter(moment(this.properties['CEP-POPULATION_CUTOFF_DATE']), 'day');
    },
    currentDateIsBetweenOpenAndDueDate() {
      const today = moment();
      const openDate = moment(this.properties['CEP-POPULATION_SUBMISSION_OPEN_DATE']);
      const dueDate = moment(this.properties['CEP-POPULATION_SUBMISSION_DUE_DATE']);

      return today.isSameOrAfter(openDate, 'day') && today.isSameOrBefore(dueDate, 'day');
    },
    isSubmissionDueDateExceeded() {
      const today = moment();
      return today.isAfter(moment(this.properties['CEP-POPULATION_SUBMISSION_DUE_DATE']));
    },
    isCepRosterAvailable() {
      if (!this.locations.length) {
        return false;
      }

      if ((this.cepSummary.populationStatus === 'A' || this.cepSummary.populationStatus === 'D')
        && this.isSubmissionDueDateExceeded) {
        return true;
      }

      return this.currentDateIsBetweenOpenAndDueDate;
    },
    refreshIsAvailable() {
      if (!this.locations.length) {
        return false;
      }

      if ((this.cepSummary.populationStatus === 'A' || this.cepSummary.populationStatus === 'D')
        && this.isSubmissionDueDateExceeded) {
        return false;
      }
      return this.schoolYear === this.currentSchoolYear && !this.isStateUser
        && this.hasModule('DRMCH:CEP:POPULATION:SUBMIT');
    },
    canSubmit() {
      if (!this.locations.length || this.loadingSummaryError
        || this.schoolYear !== this.currentSchoolYear
        || this.isStateUser || !this.hasModule('DRMCH:CEP:POPULATION:SUBMIT')
      ) {
        return false;
      }

      const today = new Date();
      if (moment(today).isBefore(moment(this.properties['CEP-POPULATION_SUBMISSION_OPEN_DATE']))) {
        return false;
      }

      if (this.cepSummary.populationStatus === 'S'
        || (this.cepSummary.populationStatus === 'A' && this.isSubmissionDueDateExceeded)) {
        return false;
      }

      return true;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    searchParams() {
      const params = {};

      if (this.locationGroupId) {
        params.locationGroupId = this.locationGroupId;
      }
      if (this.schoolYear) {
        params.schoolYear = this.schoolYear;
      }
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    urlParams() {
      const params = {};
      params.pageNum = this.page ? this.page : undefined;
      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);
      return params;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    async approveSchool(schoolId) {
      try {
        await axios.put(`cep/${this.locationGroupId}/population/approve?schoolId=${schoolId}`);
        this.showNotification('success', ['Population data is accepted.']);
        this.getLocations();
        this.refreshSummarySection();
      } catch (error) {
        const { status } = error.response;
        if (status === 400) {
          this.showNotification('danger', [`There was a problem accepting population data. ${error.response.data.message}`]);
        } else {
          this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
        }
      }
    },
    checkApprovalStatus(loc) {
      return (
        this.isPopulationDateInvalid
        || loc.approvalStatus === 'A'
        || loc.approvalStatus === 'P'
        || this.cepSummary.populationStatus === 'O'
      );
    },
    checkRejectStatus(loc) {
      return (
        this.isPopulationDateInvalid
        || loc.approvalStatus === 'R'
        || loc.approvalStatus === 'P'
        || this.cepSummary.populationStatus === 'O'
      );
    },
    async getLocations() {
      // Prevent API call if required params are missing
      if (!this.locationGroupId || !this.schoolYear) {
        this.locations = [];
        return;
      }
      try {
        this.loading = true;
        this.loadingError = false;

        const response = await axios
          .get(`cep/${this.locationGroupId}/population/${this.schoolYear}/locations`,
            {
              params: this.urlParams,
              paramsSerializer(params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
              },
            });
        this.locations = response.data.data;
        this.totalCount = response.data.totalCount;
        this.apiSorter = response.data.sorters[0];
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async rejectSchool(schoolId) {
      try {
        await axios.put(`cep/${this.locationGroupId}/population/reject?schoolId=${schoolId}`);
        this.showNotification('success', ['Population data is rejected.']);
        this.getLocations();
        this.refreshSummarySection();
      } catch (error) {
        const { status } = error.response;
        if (status === 400) {
          this.showNotification('danger', [`There was a problem rejecting population data. ${error.response.data.message}`]);
        } else {
          this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
        }
      }
    },
    reloadPopulationData() {
      this.refreshSummarySection();
      this.getLocations();
    },
    async reopenSubmission() {
      try {
        await axios.put(`cep/${this.locationGroupId}/population/reopen-submission`);
        this.showNotification('success', ['Population submission is now permitted for LEAs.']);
        this.refreshSummarySection();
        this.getLocations();
      } catch (error) {
        const { status } = error.response;
        let apiMsg = '';
        if (status === 403) {
          apiMsg = 'Sorry, you are not authorized to access this content.';
        } else {
          apiMsg = 'There was an issue. Please try again or contact your administrator.';
        }
        this.showNotification('danger', [apiMsg]);
      }
    },
    refreshSummarySection() {
      this.$root.$emit('refreshCepSummary');
    },
    async refreshPopulation() {
      try {
        this.loading = true;
        this.resetCertify();
        await axios.post(`cep/${this.locationGroupId}/population/refresh`, {});
        this.refreshSummarySection();
        this.getLocations();
      } catch (error) {
        const { status } = error.response;
        let apiMsg = '';
        if (status === 403) {
          apiMsg = 'Sorry, you are not authorized to access this content.';
        } else if (status === 400) {
          apiMsg = `There was a problem refreshing population data. ${error.response.data.message}`;
        } else {
          apiMsg = 'There was an issue. Please try again or contact your administrator.';
        }
        this.showNotification('danger', [apiMsg]);
      } finally {
        this.loading = false;
      }
    },
    resetCertify() {
      this.hideNotification();
      this.certifyError ? this.certifyError = false : null;
    },
    async submitPopulation() {
      if (this.certified) {
        try {
          await axios.put(`cep/${this.locationGroupId}/population/certify`);
          this.showNotification('success', ['Population data has been submitted successfully.']);
          this.refreshSummarySection();
          this.getLocations();
        } catch (error) {
          const { status } = error.response;
          if (status === 400) {
            this.showNotification('danger', [`There was a problem submitting population data. ${error.response.data.message}`]);
          } else {
            this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
          }
        }
      } else {
        this.showNotification('danger', ['Please check the checkbox to proceed to submit and certify population data.']);
        this.certifyError = true;
      }
    },
    updateSorter(header) {
      this.$router.replace({ query: this.searchParams });
    },
    async generateCepRoster() {
      try {
        this.$refs.myActivity.setBusy(true);
        await axios.post(`/cep/${this.locationGroupId}/population/${this.schoolYear}/generate-roster`);
        await this.$refs.myActivity.getLatestCepRosterReport();
      } catch (error) {
        const { status } = error.response;
        let apiMsg = '';
        if (status === 403) {
          apiMsg = 'Sorry, you are not authorized to generate CEP Roster.';
        } else if (status === 400) {
          apiMsg = `There was a problem generating the report. ${error.response.data.message}`;
        } else {
          apiMsg = 'There was an issue. Please try again or contact your administrator.';
        }
        this.showNotification('danger', [apiMsg]);
      }
    },
    onRefreshMyActivity(value) {
      this.cepRosterGenerationFinished = value;
    },
  },
};
</script>

<style lang="sass" scoped>
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  a.generate-cep-roster-button
    font-size: .85rem
  .certify-row
    justify-content: flex-end
    align-items: center
  .certify-description
    margin-right: 1rem
  .action-padding
    padding-left: 0.5rem
</style>
